import { Box, Typography, useTheme } from '@mui/material'
import React from 'react'
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceArea,
  ResponsiveContainer
} from 'recharts'
import { ContentType } from 'recharts/types/component/DefaultLegendContent'
import {
  NameType,
  ValueType
} from 'recharts/types/component/DefaultTooltipContent'
import { ContentType as TooltipContentType } from 'recharts/types/component/Tooltip'
import { decimalToString } from 'utils/decimal'
import { IDataEntry, IReferenceArea, PeriodTypes } from '../types/types'
import {
  groupLast12Months,
  groupLast24Hours,
  groupLast30Days,
  groupLast7Days,
  timeZone
} from './utils/processTimeIntervals'
import dayjs, { Dayjs } from 'dayjs'

/**
 * Interface for the GraphLinesWithBar component props.
 */
interface IGraphLinesWithBarProps {
  /** Array of data entries for the graph */
  data: IDataEntry[][]
  /** Type of data being displayed (e.g., '%', 'bpm', 'mg/dL') */
  dataType: string
  /** Loading state for the graph */
  loading: boolean
  /** Periods for which the data is displayed */
  periods: PeriodTypes
  /** Optional reference areas to highlight specific ranges in the graph */
  referenceAreas?: IReferenceArea[]
  /** Optional function to format the value displayed in the graph */
  formatValue?: (value: string | number | null) => string | number | null
  /** Optional y-axis domain for the graph */
  yAxisDomain?: [number, number]
  /** Optional flag to indicate if the graph has minimum and maximum values */
  hasMinAndMax?: boolean
  /** Optional flag to indicate if the average should be floating point */
  floatingAvg?: boolean
  /** Optional reference date for the graph */
  referenceDate?: Dayjs | null
}

const chartPeriodSuffix = {
  day: ' hora',
  week: ' dia',
  month: ' dia',
  year: ' mês'
}

/**
 * GraphLinesWithBar component for displaying a combination of line and bar charts.
 * @param {IGraphLinesWithBarProps} - Props for the component.
 * @returns JSX.Element
 * @example
 * <GraphLinesWithBar
 *  data={dataEntries}
 *  periods="month"
 *  dataType="%"
 *  loading={false}
 *  referenceAreas={[{ y1: 50, y2: 100, fill: 'red' }]}
 *  formatValue={(value) => `${value}%`}
 *  yAxisDomain={[0, 100]}
 *  hasMinAndMax={true}
 * />
 */
const GraphLinesWithBar: React.FC<IGraphLinesWithBarProps> = ({
  data,
  periods,
  dataType,
  loading,
  referenceAreas,
  formatValue,
  yAxisDomain,
  hasMinAndMax,
  floatingAvg = false,
  referenceDate
}) => {
  const theme = useTheme()
  const colors = [
    {
      bar: theme.palette.secondary.main,
      line: theme.palette.primarySoft.main
    },
    {
      bar: '#57637d',
      line: '#71A0FF'
    }
  ]

  const CustomLegend: ContentType = ({ payload }) => {
    return (
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {payload?.map((entry: any, index: number) => (
          <li key={`item-${index}`} style={{ color: entry.color }}>
            <span style={{ marginRight: 8, fontWeight: 'bold' }}>⬤</span>
            {entry.value.includes('limits') &&
              `${index > 0 ? data[index]?.[0]?.name : ''} Máxima/mínima por período`}
            {entry.value.includes('avg') && 'Média por período'}
          </li>
        ))}
      </ul>
    )
  }

  const CustomTooltip: TooltipContentType<ValueType, NameType> = ({
    active,
    payload,
    label
  }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            background: theme.palette.background.default,
            p: 1,
            gap: 2,
            borderRadius: 1,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Typography fontWeight='bold'>{`${label}`}</Typography>
          {datasets.map((_, dataIndex) => (
            <React.Fragment key={dataIndex}>
              {payload?.map((entry: any, index: number) => (
                <React.Fragment key={index}>
                  {hasMinAndMax && entry.name === `${dataIndex}_limits` && (
                    <Typography sx={{ color: entry.color }}>
                      <b>Máxima/Mínima:</b>{' '}
                      {`${formatValue ? formatValue(entry.value?.[0]) : entry.value?.[0]}${dataType} - ${formatValue ? formatValue(entry.value?.[1]) : entry.value?.[1]}${dataType}`}
                    </Typography>
                  )}
                  {entry.name === `${dataIndex}_avg` && (
                    <Typography sx={{ color: entry.color }}>
                      <b>Média {chartPeriodSuffix[periods]}:</b>{' '}
                      {`${decimalToString(entry.value)}${dataType}`}
                    </Typography>
                  )}
                </React.Fragment>
              ))}
              <div style={{ height: 5 }} />
            </React.Fragment>
          ))}
        </Box>
      )
    }

    return null
  }

  const date = referenceDate?.tz(timeZone) ?? dayjs().tz(timeZone)

  const groupedData = {
    day: (data: IDataEntry[]) => groupLast24Hours(data, date, floatingAvg),
    week: (data: IDataEntry[]) => groupLast7Days(data, date, floatingAvg),
    month: (data: IDataEntry[]) => groupLast30Days(data, date, floatingAvg),
    year: (data: IDataEntry[]) => groupLast12Months(data, date)
  }

  const datasets = data.map(hv => groupedData[periods](hv))

  const dataset = datasets.reduce(
    (acc, row, rowIndex) => {
      row.forEach((entry, colIndex) => {
        if (!acc[colIndex]) acc[colIndex] = {}
        acc[colIndex]['label'] = entry.label
        acc[colIndex][`${rowIndex}_limits`] = entry.limits ?? null
        acc[colIndex][`${rowIndex}_avg`] = entry.avg ?? null
      })
      return acc
    },
    [] as Record<string, number[] | string | number | null>[]
  )

  return (
    <ResponsiveContainer>
      <ComposedChart
        width={1000}
        height={400}
        data={dataset}
        margin={{
          top: 20,
          right: 20,
          bottom: 20,
          left: 20
        }}
        style={
          loading
            ? {
                opacity: 0.5, // Gráfico opaco durante o loading
                transition: 'opacity 0.5s ease-in-out' // Transição suave
              }
            : {}
        }
      >
        <CartesianGrid stroke={theme.palette.divider} />
        <XAxis dataKey='label' />
        <YAxis domain={yAxisDomain ?? [0, 100]} />
        <Tooltip content={CustomTooltip} />
        {!!datasets.length && <Legend content={CustomLegend} />}
        {/* TODO: Remove min/max in graphs */}
        {/* {datasets.map((_, index) => (
        <Bar
          key={`${index}_limits`}
          dataKey={`${index}_limits`}
          barSize={3}
          fill={colors[index % colors.length].bar}
          max={100}
          min={0}
          shape={(props: any) => {
            const { x, y, width, payload, height } = props
            const minHeight = 5
            const useMinHeight = height === 0
            const lineHeight = !useMinHeight ? height : -minHeight
            const middleX = x + width / 2
            const topY = y + lineHeight
            const bottomY = !useMinHeight ? y : y + minHeight
            const customStrokeWidth = hasMinAndMax ? 2 : 0

            if (payload[`${index}_limits`] !== null) {
              return (
                <g>
                  //* vertical line
                  <line
                    x1={middleX}
                    x2={middleX}
                    y1={topY}
                    y2={bottomY}
                    stroke={colors[index % colors.length].bar}
                    strokeWidth={customStrokeWidth}
                  />
                  //* top bar
                  <line
                    x1={middleX - width}
                    y1={topY}
                    x2={middleX + width}
                    y2={topY}
                    stroke={colors[index % colors.length].bar}
                    strokeWidth={customStrokeWidth}
                  />
                  //* bottom bar
                  <line
                    x1={middleX - width}
                    y1={bottomY}
                    x2={middleX + width}
                    y2={bottomY}
                    stroke={colors[index % colors.length].bar}
                    strokeWidth={customStrokeWidth}
                  />
                </g>
              )
            }

            return <g />
          }}
        />
      ))} */}
        {datasets.map((_, index) => (
          <Line
            key={`${index}_avg`}
            type='monotone'
            dataKey={`${index}_avg`}
            stroke={colors[index % colors.length].line}
            strokeWidth={3}
            markerWidth={1}
          />
        ))}
        {referenceAreas?.map((ref, index) => (
          <ReferenceArea key={index} opacity={0.2} {...ref} />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  )
}

export default GraphLinesWithBar
